/**
 * @file CompanyPaymentMethodsCard.tsx
 * @brief Company Admin → list / add / default / remove corporate cards.
 *
 * @author DES
 * @date 19-06-2026
 * @updated 29-07-2026 — GridBlock flush layout
 */

import { SkeletonRows } from "@/components/ui/Skeleton";
import { EmptyState } from "@/components/feedback/EmptyState";
import {
  CreditCard,
  Ellipsis,
  Star,
  Trash2,
} from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/AlertDialog";
import { Button } from "@/components/ui/Button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/DropdownMenu";
import {
  GridBand,
  GridCell,
} from "@/features/analytics/components/GridBlock";
import { ApiClientError } from "@/shared/api/client";
import { useAuth } from "@/shared/auth/auth-context";
import { toast } from "@/shared/hooks/useToast";
import { isStripeConfigured } from "@/shared/stripe/stripeClient";
import {
  CardConfirmStatus,
  ConfirmCardBanner,
  ConfirmWithBankLabel,
  useConfirmSavedCard,
} from "@/shared/billing/ConfirmSavedCard";
import {
  companyBillingKeys,
  createCompanyConfirmIntent,
  mapPaymentMethods,
  useCompanyPaymentMethods,
  useDeleteCompanyPaymentMethod,
  useSetCompanyDefaultPaymentMethod,
} from "../api";
import { AddCompanyCardDialog } from "./AddCompanyCardDialog";
import { CardTiles } from "@/shared/billing/CardTiles";

function formatBrand(brand: string): string {
  const b = brand.trim();
  if (!b) return "Card";
  return b.charAt(0).toUpperCase() + b.slice(1);
}

function resolveCompanyId(
  user: ReturnType<typeof useAuth>["user"],
): string | null {
  return user?.company_id ?? user?.companyId ?? null;
}

export function CompanyPaymentMethodsCard() {
  const { user } = useAuth();
  const companyId = resolveCompanyId(user);
  const companyName = user?.company_name ?? "your company";

  const query = useCompanyPaymentMethods(companyId);
  const setDefault = useSetCompanyDefaultPaymentMethod();
  const remove = useDeleteCompanyPaymentMethod();

  const [addOpen, setAddOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{
    id: string;
    label: string;
  } | null>(null);

  const cards = useMemo(() => mapPaymentMethods(query.data), [query.data]);
  const stripeReady = isStripeConfigured();
  // "Confirm with your bank" on a company card (21-10-2026): an admin lands
  // here from the email when a phone hold needed the bank's check.
  const qc = useQueryClient();
  const createIntent = useCallback(
    (pm: string) => createCompanyConfirmIntent(companyId ?? "", pm),
    [companyId],
  );
  const refreshCards = useCallback(
    () => void qc.invalidateQueries({ queryKey: companyBillingKeys.paymentMethods(companyId ?? "") }),
    [qc, companyId],
  );
  const bank = useConfirmSavedCard({ createIntent, onConfirmed: refreshCards });

  const run = async (action: () => Promise<unknown>, title: string) => {
    try {
      await action();
      toast({ variant: "success", title });
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Action failed",
        description:
          err instanceof ApiClientError ? err.message : "Please try again.",
      });
    }
  };

  if (!companyId) {
    return (
      <>
        <GridBand>
          <h3 className="text-base font-semibold tracking-tight">
            Payment methods
          </h3>
          <p className="mt-1 text-sm text-muted-foreground">
            Your account is not linked to a company. Contact support if this is
            unexpected.
          </p>
        </GridBand>
      </>
    );
  }

  return (
    <>
      <GridBand className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h3 className="text-base font-semibold tracking-tight">
            Payment methods
          </h3>
          <p className="mt-1 text-sm text-muted-foreground">
            Corporate cards for {companyName}. Used when employees bill sessions
            to the company.
          </p>
        </div>
      </GridBand>

      <GridCell padded={false}>
        {stripeReady && !query.isLoading ? (
          <ConfirmCardBanner
            cards={cards}
            defaultId={cards.find((c) => c.isDefault)?.id ?? null}
            brandLabel={formatBrand}
            states={bank.states}
            onConfirm={(id) => void bank.confirm(id)}
            onAddCard={() => setAddOpen(true)}
          />
        ) : null}
        {!stripeReady ? (
          <div className="border-b border-border/60 px-4 py-3 text-sm text-amber-900 dark:text-amber-100 md:px-5">
            Stripe is not configured for this panel. Set{" "}
            <code className="text-xs">VITE_STRIPE_PUBLISHABLE_KEY</code> in{" "}
            <code className="text-xs">.env.local</code>.
          </div>
        ) : null}

        {query.isLoading ? (
          <SkeletonRows rows={2} twoLine />
        ) : query.isError ? (
          <div className="px-4 py-3 text-sm text-destructive md:px-5">
            Couldn't load saved cards.
          </div>
        ) : cards.length === 0 ? (
          <EmptyState
            shape="invoice"
            title="No company cards yet"
            description="Add a card so employees can start corporate billing sessions."
            action={
              stripeReady ? (
                <button type="button" onClick={() => setAddOpen(true)} className="inline-flex h-12 items-center gap-2 rounded-full bg-brand px-6 text-[15px] font-semibold text-white transition-colors hover:bg-brand/90">
                  <CreditCard className="h-5 w-5" />
                  Add first card
                </button>
              ) : null
            }
          />
        ) : (
          <CardTiles
            cards={cards}
            below={(card) => <CardConfirmStatus state={bank.states[card.id]} />}
            onAdd={stripeReady ? () => setAddOpen(true) : undefined}
            addDisabled={query.isLoading}
            menu={(card) => (
                <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            aria-label={`Actions for card ending ${card.last4}`}
                          >
                            <Ellipsis className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {stripeReady ? (
                            <DropdownMenuItem
                              disabled={bank.states[card.id]?.status === "running"}
                              onClick={() => void bank.confirm(card.id)}
                            >
                              <ConfirmWithBankLabel />
                            </DropdownMenuItem>
                          ) : null}
                          {!card.isDefault ? (
                            <DropdownMenuItem
                              onClick={() =>
                                void run(
                                  () =>
                                    setDefault.mutateAsync({
                                      companyId,
                                      paymentMethodId: card.id,
                                    }),
                                  "Default card updated",
                                )
                              }
                            >
                              <Star className="h-4 w-4" /> Set as default
                            </DropdownMenuItem>
                          ) : null}
                          <DropdownMenuItem
                            className="text-destructive focus:text-destructive"
                            onClick={() =>
                              setDeleteTarget({
                                id: card.id,
                                label: `${formatBrand(card.brand)} •••• ${card.last4}`,
                              })
                            }
                          >
                            <Trash2 className="h-4 w-4" /> Remove
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
            )}
          />
        )}
      </GridCell>

      <AddCompanyCardDialog
        companyId={companyId}
        open={addOpen}
        onOpenChange={setAddOpen}
        setAsDefault
      />

      <AlertDialog
        open={Boolean(deleteTarget)}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove card?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget?.label} will be detached from {companyName}.
              Corporate sessions may fail if no other card is on file.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (!deleteTarget) return;
                void run(
                  () =>
                    remove.mutateAsync({
                      companyId,
                      paymentMethodId: deleteTarget.id,
                    }),
                  "Card removed",
                );
                setDeleteTarget(null);
              }}
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
